import { AfterContentInit, booleanAttribute, Component, ContentChild, ElementRef, inject, Input, NgZone, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControlDirective, FormControlName, NgModel, UntypedFormControl } from '@angular/forms';
import { HIS_VIEW_CONTEXT } from '../../../core/directive/his-dd-contexts.directive';
import { HisDdService } from '../../../core/service/base/his-dd.service';
import { HisUiService } from '../../../core/service/base/his-ui.service';
import { HisUidService } from '../../../core/service/base/his-uid.service';
import { HisDdValidator } from '../../../core/validator/his-dd-validator';
import { Language } from '../../../translation/model/language';
import { HisTranslationService } from '../../../translation/service/his-translation.service';
import { HisDdValidatorFactory } from './../../../core/validator/his-dd-validator-factory';
import { HisDdPopupComponent } from '../dd-popup/his-dd-popup.component';
import { HisBaseGlobalsService } from '../../../core/service/base/his-base-globals.service';

export interface LabelProvider {
    ddLabeltext: string;
}

export interface HelptextProvider {
    ddHelptext: string;
}

export interface RequiredProvider {
    required: boolean;
}

/**
 * Panel, Label and message component for input elements.
 */
@Component( {
    selector: 'his-plam',
    templateUrl: './his-plam.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class HisPlamComponent implements AfterContentInit {
    private validatorFactory = inject( HisDdValidatorFactory );
    private hisTranslationService = inject( HisTranslationService );
    private hisDdService = inject( HisDdService );
    private hisViewContext = inject( HIS_VIEW_CONTEXT );
    hisBaseGlobalsService = inject( HisBaseGlobalsService );
    private zone = inject( NgZone );
    private uiService = inject( HisUiService );


    public static readonly REQUIRED_INDICATOR = '*';

    @Input() key: string;
    @Input() label: string;
    @Input() required: boolean;
    @Input() helptext: string;
    @Input( { transform: booleanAttribute } ) displayHelptext = false;
    @Input( { transform: booleanAttribute } ) showDebug = false;
    @Input( { transform: booleanAttribute } ) debugAvailable = false;
    @Input( { transform: booleanAttribute } ) showLabelTop = false;
    @Input( { transform: booleanAttribute } ) renderLabelAsDiv = false;
    @Input() backendError: string;
    @Input() tabindex = -1;
    @Input( { transform: booleanAttribute } ) noPlamStyle = false;
    /**
     * iff <pre>false</pre>, no helptext will ever be shown for this plam
     */
    @Input( { transform: booleanAttribute } ) showHelptext = true;

    @Input() isCheckboxRadio: boolean;
    isCheckboxRadioAfterLable: boolean;

    contentId: string;

    hasBackendErrors: boolean;

    @ContentChild( 'content' ) contentRef: any;

    @ContentChild( NgModel ) ngModelRef: NgModel;

    @ContentChild( HisDdValidator ) ddValidator: HisDdValidator;

    @ContentChild( FormControlDirective ) ctrlRef: FormControlDirective;
    @ContentChild( FormControlName ) ctrlNameRef: FormControlName;

    @ViewChild( HisDdPopupComponent ) private ddPopup: HisDdPopupComponent;

    /** the language we are currently translating to
     *  will be fetched from hisTranslationService
     */
    public translationLanguage: Language;

    private formControl: UntypedFormControl;

    private errorMessageBeforeSave = '';

    get requiredIndicator(): string {
        return HisPlamComponent.REQUIRED_INDICATOR;
    }

    ngAfterContentInit(): void {
        this.formControl = this.ctrlRef?.form || this.ctrlNameRef?.control || undefined;

        // if translatable subscribe to changes on translationLanguage
        if ( this.isTranslatable() ) {
            this.initializeTranslatable();
        }

        // if Input is his-editor, set nativeElement from hostRef
        if ( this.contentRef.hostRef?.nativeElement?.tagName === 'HIS-EDITOR' ) {
            this.contentRef.nativeElement = this.contentRef.hostRef.nativeElement;
        }

        // if Input is checkbox
        if ( this.contentRef.nativeElement ) {
            if ( this.contentRef.nativeElement.type === 'checkbox' || this.contentRef.nativeElement.type === 'radio' ) {
                this.isCheckboxRadio = true;
                this.isCheckboxRadioAfterLable = this.contentRef.nativeElement.classList.contains( 'checkboxAfterLabel' );
            }
        }

        // console.log( 'contentRef =', this.contentRef );
        if ( this.contentRef.widget ) {
            // The component is used within a generic search page -> extract attributes from the search config file.
            this.helptext = this.contentRef.widget.helptext;
            this.required = this.contentRef.widget.required;
        }

        // Apply label from child component if none is set.
        if ( !this.label && !this.key ) {
            this.label = this.getContentElement().label;
        }

        // Get the id to be used by the plam label 'for' attribute from the content element if provided.
        if ( this.contentRef.getHtmlFocusElementId ) {
            this.contentId = this.contentRef.getHtmlFocusElementId();
            // console.log( 'PLAM obtained content id', this.contentId );
            return;
        }

        // No id provided by the content -> generate one and put it into the content element.
        this.contentId = HisUidService.get( 'plam.' );
        if ( this.contentRef.nativeElement ) {
            // Text input fields have the 'id' attribute.
            this.contentRef.nativeElement.id = this.contentId;
            return;
        }

        // p-dropdown, p-autocomplete etc. expect the id in the 'inputId' attribute.
        this.contentRef.inputId = this.contentId;
    }

    toggleShowHelptext() {
        this.displayHelptext = !this.displayHelptext;
        this.zone.runOutsideAngular( () => {
            setTimeout( () =>
                this.focus(), 5 );
        } );
    }

    focus() {
        if ( this.contentRef.nativeElement ) {
            if ( this.contentRef.nativeElement.parentNode.parentNode ) {
                if ( this.contentRef.nativeElement.parentNode.parentNode.nextElementSibling ) {
                    if ( this.contentRef.nativeElement.parentNode.parentNode.nextElementSibling.innerHTML.indexOf( 'plamHelptext' ) > -1 ) {
                        this.contentRef.nativeElement.parentNode.parentNode.nextElementSibling.children[1].focus();
                    }
                }
            }
        }
        else if ( this.contentRef.el.nativeElement ) {
            if ( this.contentRef.el.nativeElement.parentElement.parentNode ) {
                if ( this.contentRef.el.nativeElement.parentElement.parentNode.nextElementSibling ) {
                    if ( this.contentRef.el.nativeElement.parentElement.parentNode.nextElementSibling.innerHTML.indexOf( 'plamHelptext' ) > -1 ) {
                        this.contentRef.el.nativeElement.parentElement.parentNode.nextElementSibling.children[1].focus();
                    }
                }
            }
        }
    }

    toggleShowDebug() {
        this.showDebug = !this.showDebug;
    }

    isRequired(): boolean {

        if ( !this.getDdContexts() && ( this.formControl && this.formControl.validator ) ) {
            const requiredValidatorTestControl = new UntypedFormControl();
            requiredValidatorTestControl.markAsDirty();
            return !!this.formControl.validator( requiredValidatorTestControl )?.required;
        }
        return this.required || this.getContentElement().required;
    }

    getHelptext(): string {
        if ( !this.showHelptext ) {
            return '';
        }

        if ( this.contentRef.widget && this.contentRef.widget.multiSearchCriterion && this.contentRef.widget.multiSearchCriterionLabels ) {
            return this.getHelptextForMultiSearchCriterion( this.contentRef.widget.multiSearchCriterionLabels );
        }

        if ( this.helptext || this.getContentElement().ddHelptext ) {
            return this.uiService.translate( this.helptext ) || this.uiService.translate( this.getContentElement().ddHelptext );
        }

        const labelKey = this.contentRef.widget?.label || this.getLabeltext();
        if ( labelKey ) {
            const helptextKey = `${labelKey}:helptext`;

            //XXX use elvis here to not break tests which use mocks for HisUiService (https://hiszilla.his.de/hiszilla/show_bug.cgi?id=312525#c9)
            return this.uiService.translateOrEmpty?.( helptextKey );
        }

        return '';
    }

    private getHelptextForMultiSearchCriterion( multiSearchCriterionLabels: string ): string {
        const labelsAsI18n = multiSearchCriterionLabels.split( '|' );
        let texts = [];
        for ( let labelAsI18n of labelsAsI18n ) {
            texts.push( this.uiService.translate( labelAsI18n ) );
        }
        const params = texts.join( ', ' );
        let helptext = this.uiService.translate( 'genericSearch.fieldIsMultiSearchCriterion', params );
        return helptext;
    }



    getLabeltext(): string {
        if ( !this.noPlamStyle ) {
            return this.key || this.getContentElement().ddLabeltext;
        }
        return '';
    }

    getBackendError(): string {
        return this.backendError || this.getContentElement().ddBackendError;
    }

    isHidden(): boolean {
        return ( this as any ).ddHidden || this.getContentElement().ddHidden || this.isWidgetDdHidden();
    }

    /**
     * For search widgets (generic search) the actual input is nested inside the widget component,
     * so the plam cannot read the 'ddHidden' property written by the dd directive.
     * Fall back to the hidden flag of the data dictionary entry (DD key = widget label).
     * The template then hides the whole plam row, except in dataDictionaryEditMode.
     */
    private isWidgetDdHidden(): boolean {
        const contexts = this.getSearchWidgetDdContexts();
        if ( !contexts ) {
            return false;
        }
        const ddEntry = this.hisDdService.getMergedEntry( this.contentRef.widget.label, contexts );
        return !!ddEntry?.hidden;
    }

    getContentElement(): any {
        if ( this.contentRef instanceof ElementRef ) {
            return this.contentRef.nativeElement;
        }

        return this.contentRef;
    }

    getErrors(): any {
        if ( this.ngModelRef ) {
            return this.ngModelRef.control.errors;
        } else if ( this.isReactiveForm() ) {
            if ( this.ddValidator ) {
                return this.ddValidator.validate( this.formControl ) || this.formControl.errors;
            }
            return this.formControl.errors;
        }
        return null;
    }

    // deprecated
    // ValidationMessages wollen wir nicht verwenden
    getValidationMessage(): string {
        if ( this.getContentElement() ) {
            let msg = ( this.getContentElement() as any ).validationMessage;
            if ( msg != null && ( msg as string ).length > 0 ) {
                return msg;
            }
        }
        return null;
    }

    hasErrors(): boolean {
        return this.getErrors() != null;
        // return this.getValidationMessage() != null || this.getErrors() != null;
    }

    shouldDisplayErrors(): boolean {
        return this.isControlDirty();
    }

    isControlDirty(): boolean {
        if ( this.ngModelRef && !this.ngModelRef.control.errors?.backendError ) {
            this.hasBackendErrors = false;
            this.tabindex = -1;
            return this.ngModelRef.control.dirty;
        } else if ( this.ngModelRef && this.ngModelRef.control.errors?.backendError ) {
            this.hasBackendErrors = true;
            if ( this.noPlamStyle ) {
                this.tabindex = 0;
            }
        } else if ( this.isReactiveForm() ) {
            return this.formControl.dirty;
        }
        return false;
    }

    /**
     * Sucht die erste Validierungs- bzw. Fehlermeldung für das Inputfield heraus
     * Achtung: Neuerdings wollen wir ValidationMessages nicht verwenden!
     */
    getFirstErrorMessage(): string {
        let msg = null; // this.getValidationMessage();

        if ( !msg ) {
            let errors = this.getErrors();
            if ( errors && Object.keys( errors ).length > 0 ) {
                let errorType = Object.keys( errors )[0]; // standardmäßig ersten Fehler anzeigen
                if ( errors.backendError ) {
                    errorType = 'backendError'; // ... aber Backend-Fehler bevorzugen, falls vorhanden
                    this.hasBackendErrors = true;
                    if ( this.noPlamStyle ) {
                        this.tabindex = 0;
                    }
                }

                // Message ermitteln
                let state = errors[errorType];
                if ( state.message ) {
                    msg = state.message;
                } else {
                    msg = this.validatorFactory.getMessageForError( errorType, state );
                }
            }
        }
        return msg;
    }

    getModelValue(): any {
        if ( this.ngModelRef ) {
            return this.ngModelRef.value;
        } else if ( this.isReactiveForm() ) {
            return this.formControl.value;
        }
        return null;
    }

    getDdIdentifier(): string {
        return ( this as any ).ddIdentifier || this.getContentElement().ddIdentifier || this.contentRef?.widget?.label; // generic search: DD-Key = Kriteriums-Label
    }

    getDdContexts(): string[] {
        return ( this as any ).hisDdContexts || this.getContentElement().hisDdContexts || this.getSearchWidgetDdContexts();
    }

    /**
     * Returns the DD contexts for search widgets (generic search), or undefined for all other content.
     * The plam cannot read the contexts from the content element, because it is a component instance
     * while the dd directive writes them onto the nested native input.
     * Uses the same contexts the dd directive applies: DEFAULT plus the implicit view context
     * (HIS_VIEW_CONTEXT, see his-dd-contexts.directive).
     */
    private getSearchWidgetDdContexts(): string[] | undefined {
        if ( !this.contentRef?.widget ) {
            return undefined;
        }
        return [ 'DEFAULT', this.hisViewContext() ];
    }

    getDataSource(): string {
        let contentElement = this.getContentElement();
        if ( contentElement ) {
            let key = 'hisDataSource';
            return contentElement[key];
        }
        return null;
    }

    isTranslatable(): boolean {
        return this.contentRef && this.contentRef.nativeElement && this.contentRef.nativeElement.isTranslatable;
    }

    initializeTranslatable() {
        this.hisTranslationService.translationLanguage$.subscribe( ( translationLanguage: Language ) => {
            this.translationLanguage = translationLanguage;
        } );
    }

    getTranslationLabel( translationLanguage: string ): string {
        let labelKey = 'cs.sys.hisng.translationLabel_' + translationLanguage;
        return labelKey;
    }

    private isReactiveForm(): boolean {
        return !!this.formControl;
    }

    hasErrorMessageBeforeSave(): boolean {
        return ( this.errorMessageBeforeSave?.length > 0 ) || false;
    }

    getErrorMessageBeforeSave(): string {
        return this.errorMessageBeforeSave;
    }

    setErrorMessageBeforeSave( msgs: string ) {
        this.errorMessageBeforeSave = msgs;
    }

    /**
     * Positioniert die Data-Dictionary-Popup-Box beim Erscheinen horizontal mittig am Mauszeiger.
     * Vertikal bleibt sie über CSS (top: 100%) unterhalb des Feldes.
     */
    onMouseEnter( event: MouseEvent ): void {
        const containerEl = ( event.currentTarget as HTMLElement );
        const containerRect = containerEl.getBoundingClientRect();
        const offsetX = event.clientX - containerRect.left;
        this.ddPopup?.position( offsetX );
    }

}
